import { createContext, useEffect, useState } from "react";

export type ToasterType = "info" | "success" | "warning" | "error" | "default";
export type TimeSpan = "short" | "medium" | "long";

export type ToasterState = {
  isOpen: boolean;
  message: string;
  timeSpan: TimeSpan;
  type: ToasterType;
};

export const ToasterContext = createContext<
  [
    ToasterState,
    (message: string, timeSpan: TimeSpan, type: ToasterType) => void,
    () => void,
  ]
>([
  { isOpen: false, message: "", timeSpan: "medium", type: "default" },
  () => {},
  () => {},
]);

export const ToasterProvider = ({ children }: any) => {
  const [toasterState, setToasterState] = useState<ToasterState>({
    isOpen: false,
    message: "",
    timeSpan: "medium",
    type: "default",
  });
  const toasterTimeMap = new Map<string, number>([
    ["short", 1600],
    ["medium", 3600],
    ["long", 5400],
  ]);

  const openToaster = (
    message: string,
    timeSpan: TimeSpan,
    type: ToasterType,
  ) => {
    setToasterState({
      message: message,
      isOpen: true,
      timeSpan: timeSpan,
      type,
    });
  };

  const closeToaster = () => {
    setToasterState({
      isOpen: false,
      message: "",
      timeSpan: "medium",
      type: "default",
    });
  };

  useEffect(() => {
    if (toasterState.isOpen) {
      setTimeout(
        () => {
          setToasterState((prevState) => ({ ...prevState, isOpen: false }));
        },
        toasterTimeMap.get(toasterState.timeSpan) ?? 3600,
      );
    }
  }, [toasterState]);

  return (
    <ToasterContext.Provider value={[toasterState, openToaster, closeToaster]}>
      {children}
      {toasterState.isOpen && (
        <div className="toaster">{toasterState.message}</div>
      )}
    </ToasterContext.Provider>
  );
};
